/** A tray panel's row anatomy: title + badge, an optional meta line, right-aligned actions. */

import type { ReactNode } from "react";

export interface TrayRowProps {
  /** Truncates to one line. The identifying name. */
  readonly title: ReactNode;
  /** Pinned to the right of the title line: a status chip. */
  readonly badge?: ReactNode;
  /** Its own line under the title, wrapping anywhere. Mono and chips go here. */
  readonly meta?: ReactNode;
  /** A second line under `meta`, for a row that carries a remedy or a second fact. */
  readonly submeta?: ReactNode;
  /** Right-aligned, its own line, never sharing a line with the title or meta. */
  readonly actions?: ReactNode;
  /** Full-width content below everything else: an inline edit form, a candidate list. */
  readonly detail?: ReactNode;
  readonly dim?: boolean;
  readonly className?: string;
}

function rowClass(dim: boolean | undefined, className: string | undefined): string {
  return ["cx-tray-row", dim === true ? "cx-tray-row-dim" : "", className ?? ""]
    .filter((one) => one !== "")
    .join(" ");
}

export function TrayRow({ title, badge, meta, submeta, actions, detail, dim, className }: TrayRowProps) {
  return (
    <div className={rowClass(dim, className)}>
      <div className="cx-tray-row-title">{title}</div>
      {badge === undefined ? null : <div className="cx-tray-row-badge">{badge}</div>}
      {meta === undefined ? null : <div className="cx-tray-row-meta">{meta}</div>}
      {submeta === undefined ? null : <div className="cx-tray-row-meta">{submeta}</div>}
      {actions === undefined ? null : <div className="cx-tray-row-actions">{actions}</div>}
      {detail === undefined ? null : <div className="cx-tray-row-detail">{detail}</div>}
    </div>
  );
}

export interface MappingRowProps {
  readonly sourceName: ReactNode;
  readonly sourceDetail?: ReactNode;
  readonly targetName: ReactNode;
  readonly targetDetail?: ReactNode;
  /** A small muted tag at the end of the mapping, such as "from a bundle". */
  readonly origin?: ReactNode;
  /** A muted line under the mapping, above the actions: a reason it is off, for instance. */
  readonly note?: ReactNode;
  readonly actions?: ReactNode;
  readonly detail?: ReactNode;
  readonly dim?: boolean;
}

/** A wire's mapping: source above target, stacked, so a narrow tray never crushes an address. */
export function MappingRow({
  sourceName,
  sourceDetail,
  targetName,
  targetDetail,
  origin,
  note,
  actions,
  detail,
  dim,
}: MappingRowProps) {
  return (
    <div className={rowClass(dim, undefined)}>
      <div className="cx-mapping">
        <div className="cx-mapping-line">
          <span className="cx-mapping-name">{sourceName}</span>
          {sourceDetail === undefined ? null : (
            <span className="cx-mapping-detail">{sourceDetail}</span>
          )}
        </div>
        <div className="cx-mapping-line">
          <span className="cx-mapping-arrow" aria-hidden="true">
            {"->"}
          </span>
          <span className="cx-mapping-name">{targetName}</span>
          {targetDetail === undefined ? null : (
            <span className="cx-mapping-detail">{targetDetail}</span>
          )}
        </div>
      </div>
      {origin === undefined ? null : <div className="cx-mapping-origin">{origin}</div>}
      {note === undefined ? null : <div className="cx-tray-row-meta">{note}</div>}
      {actions === undefined ? null : <div className="cx-tray-row-actions">{actions}</div>}
      {detail === undefined ? null : <div className="cx-tray-row-detail">{detail}</div>}
    </div>
  );
}
